/**
 * A mirror of `apps/proposal/signature_slots.py`: the same rules under the same
 * numbers, so a rule added or retired on one side alone shows as a gap. The
 * backend returns an English sentence; this side returns the rule's id and
 * leaves the wording to the caller, which has the author's language.
 */
import type { TFunction } from "react-i18next"

import {
  TEMPLATE_BLOCK_TYPE,
  type TemplateBlockType
} from "../constants/template-block-type.tsx"
import type { DocumentTemplateBlock } from "../types/types.ts"

const SIGNATURE_BLOCK_TYPES: readonly TemplateBlockType[] = [
  TEMPLATE_BLOCK_TYPE.SIGNATURE_DATE,
  TEMPLATE_BLOCK_TYPE.INITIAL_SIGNATURE
]

const SIGNATURE_FIELD_TYPES = [
  "signature",
  "initials",
  "date_signed",
  "full_name",
  "text"
] as const
type SignatureFieldType = (typeof SIGNATURE_FIELD_TYPES)[number]

const PARTY_TYPES = [
  "customer",
  "subcontractor",
  "supplier",
  "internal"
] as const
type PartyType = (typeof PARTY_TYPES)[number]

type SignerSlot = {
  key: string
  name: string
  party_type: PartyType
}

type SignatureFieldDefinition = {
  key: string
  field_type: SignatureFieldType
  label: string
  required: boolean
}

// Every rule `validateSignatureBlocks` can report. The ids are the contract
// with `SIGNATURE_RULE_KEYS`; the numbers are the backend's own.
const SIGNATURE_BLOCK_RULES = [
  "slot_declared_twice", // Rule 1
  "unknown_field_type", // Rule 2
  "field_missing_key", // Rule 3
  "duplicate_field_key", // Rule 4
  "fields_without_slot", // Rule 5
  "slot_name_blank", // Rule 6
  "slot_name_not_unique", // Rule 6
  "slot_party_type_unknown" // Rule 7
] as const
type SignatureBlockRule = (typeof SIGNATURE_BLOCK_RULES)[number]

/**
 * One rule that fired, and what it fired about. Exactly one of `blockId` and
 * `slotKey` is set — the author sees a block by its position and a slot by its
 * signer's name, so naming either is the caller's job. The rest are the
 * parameters that one rule's message quotes.
 */
type SignatureBlockError = {
  blockId?: string
  slotKey?: string
  reason: SignatureBlockRule
  fieldType?: string
  fieldKey?: string
  partyType?: string
}

// The fields that must agree everywhere a slot key is declared — blocks
// sharing a key describe the same person, so they must describe them the
// same way.
const SLOT_IDENTITY_KEYS = ["key", "name", "party_type"] as const

function isSignatureBlock(block: DocumentTemplateBlock): boolean {
  return (SIGNATURE_BLOCK_TYPES as readonly string[]).includes(block.block_type)
}

/**
 * The slot a signature block declares, or `null` when it declares none. A slot
 * on a block that cannot sign is ignored rather than honoured: nothing
 * downstream could serve it, and every reader goes through here, so the answer
 * is decided in one place.
 */
function slotOf(block: DocumentTemplateBlock): SignerSlot | null {
  if (!isSignatureBlock(block)) return null
  const slot = block.config?.slot
  return slot && typeof slot === "object" && slot.key ? slot : null
}

/**
 * The fields a block declares for its slot's holder to complete. Carries
 * `slotOf`'s block-type guard, which the backend's `fields_of` leaves to its
 * callers — do not "simplify" it away.
 */
function fieldsOf(block: DocumentTemplateBlock): SignatureFieldDefinition[] {
  if (!isSignatureBlock(block)) return []
  const fields = block.config?.fields
  return Array.isArray(fields) ? fields : []
}

/**
 * The slots declared, first declaration of a key winning: blocks legitimately
 * share a slot — one signer signs on page one and initials on page three.
 * `validateSignatureBlocks` is what holds those declarations to agreeing.
 */
function declaredSlots(blocks: DocumentTemplateBlock[]): SignerSlot[] {
  const slots = new Map<string, SignerSlot>()
  for (const block of blocks) {
    const slot = slotOf(block)
    if (slot && !slots.has(slot.key)) {
      slots.set(slot.key, slot)
    }
  }
  return [...slots.values()]
}

/**
 * Everything wrong with these blocks' slots and fields, or `[]` when nothing
 * is. Same numbers, conditions and reporting order as
 * `validate_signature_blocks`. A block that declares neither a slot nor a field
 * fires nothing, so a template written before slots existed still saves.
 */
function validateSignatureBlocks(
  blocks: DocumentTemplateBlock[]
): SignatureBlockError[] {
  const errors: SignatureBlockError[] = []
  const seen = new Map<string, SignerSlot>()
  const namesSeen = new Set<string>()
  for (const block of blocks) {
    if (!isSignatureBlock(block)) continue
    const slot = slotOf(block)
    const fields = fieldsOf(block)

    if (slot) {
      const declared = seen.get(slot.key)
      if (!declared) {
        seen.set(slot.key, slot)
        // Rule 6: a slot's name is the only thing that tells its signer apart
        // in the panel, in every block's picker and in a refusal. Checked once
        // per key — blocks sharing a slot repeat its name, and that repetition
        // is not a second signer.
        const name = slot.name?.trim() ?? ""
        if (!name) {
          errors.push({ slotKey: slot.key, reason: "slot_name_blank" })
        } else if (namesSeen.has(name)) {
          errors.push({ slotKey: slot.key, reason: "slot_name_not_unique" })
        } else {
          namesSeen.add(name)
        }

        // Rule 7: a slot's party type is one an envelope can address. Checked
        // once per key like Rule 6: it is part of the slot's identity, and
        // Rule 1 holds a repeat of the key to declaring it the same way.
        const partyType: string = slot.party_type ?? ""
        if (!(PARTY_TYPES as readonly string[]).includes(partyType)) {
          errors.push({
            slotKey: slot.key,
            reason: "slot_party_type_unknown",
            partyType
          })
        }
      } else if (
        SLOT_IDENTITY_KEYS.some((key) => declared[key] !== slot[key])
      ) {
        // Rule 1: one slot key is declared the same way everywhere.
        errors.push({ slotKey: slot.key, reason: "slot_declared_twice" })
      }
    }

    const keys = new Set<string>()
    for (const field of fields) {
      // Rule 2: a field's type is one this product knows.
      if (
        !(SIGNATURE_FIELD_TYPES as readonly string[]).includes(field.field_type)
      ) {
        errors.push({
          blockId: block.id,
          reason: "unknown_field_type",
          fieldType: field.field_type
        })
      }
      const key = field.key
      // Rule 3: a field has a key, which is how it is matched back later.
      if (!key) {
        errors.push({ blockId: block.id, reason: "field_missing_key" })
      } else if (keys.has(key)) {
        // Rule 4: no two fields in one block share a key.
        errors.push({
          blockId: block.id,
          reason: "duplicate_field_key",
          fieldKey: key
        })
      }
      keys.add(key)
    }

    // Rule 5: fields need a slot to hold them. Checked last: a bad field on
    // an unslotted block is reported for what it is first, and only then for
    // having nowhere to attach.
    if (fields.length > 0 && !slot) {
      errors.push({ blockId: block.id, reason: "fields_without_slot" })
    }
  }
  return errors
}

/**
 * A new array with every block sharing `key` updated in lockstep.
 *
 * Pure: the panel and the block editor both rebuild the `blocks` array
 * rather than mutating it, because antd compares by reference to decide
 * what to re-render.
 */
function blocksWithSlotRenamed(
  blocks: DocumentTemplateBlock[],
  key: string,
  patch: Partial<Omit<SignerSlot, "key">>
): DocumentTemplateBlock[] {
  return blocks.map((block) => {
    const slot = slotOf(block)
    if (!slot || slot.key !== key) return block
    return {
      ...block,
      config: {
        ...block.config,
        slot: { ...slot, ...patch }
      }
    }
  })
}

/**
 * A new array with `slot` and `fields` cleared from every block holding
 * `key`; every other key on the block's config, such as its title, is left
 * untouched.
 */
function blocksWithSlotRemoved(
  blocks: DocumentTemplateBlock[],
  key: string
): DocumentTemplateBlock[] {
  return blocks.map((block) => {
    const slot = slotOf(block)
    if (!slot || slot.key !== key) return block
    const { slot: _slot, fields: _fields, ...rest } = block.config
    return { ...block, config: rest }
  })
}

/**
 * A signer's display name turned into a stable slot key — `customer_1`-style,
 * de-duplicated with a numeric suffix against the keys already in use. Minted
 * once, at creation: the backend matches on the key, so a later rename must
 * never touch it (`blocksWithSlotRenamed` patches `name`/`party_type` only).
 */
function mintSlotKey(name: string, existingKeys: readonly string[]): string {
  const base =
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "signer"

  if (!existingKeys.includes(base)) return base

  let suffix = 2
  while (existingKeys.includes(`${base}_${suffix}`)) suffix += 1
  return `${base}_${suffix}`
}

/**
 * Which `t()` key writes the sentence for each rule id. Paired with the ids
 * rather than chosen apart, so a new rule cannot ship without its copy.
 */
const SIGNATURE_RULE_KEYS: Record<SignatureBlockRule, string> = {
  slot_declared_twice: "documentTemplates.signers.errors.slotDeclaredTwice",
  unknown_field_type: "documentTemplates.signers.errors.unknownFieldType",
  field_missing_key: "documentTemplates.signers.errors.fieldMissingKey",
  duplicate_field_key: "documentTemplates.signers.errors.duplicateFieldKey",
  fields_without_slot: "documentTemplates.signers.errors.fieldsWithoutSlot",
  slot_name_blank: "documentTemplates.signers.errors.slotNameBlank",
  slot_name_not_unique: "documentTemplates.signers.errors.slotNameNotUnique",
  slot_party_type_unknown:
    "documentTemplates.signers.errors.slotPartyTypeUnknown"
}

/**
 * A block id turned into what the author actually sees: its own title, or its
 * 1-based position among the template's signature blocks. A signature block's
 * title is fixed and unrendered today, so position is the live path; the raw
 * id appears nowhere in the builder.
 */
function nameSignatureBlock(
  blockId: string,
  blocks: DocumentTemplateBlock[],
  t: TFunction
): string {
  const block = blocks.find((b) => b.id === blockId)
  const title = block?.config?.title
  if (typeof title === "string" && title.trim()) return title.trim()

  const position =
    blocks.filter(isSignatureBlock).findIndex((b) => b.id === blockId) + 1
  return t("documentTemplates.signers.errors.blockPosition", {
    position: position || 1
  })
}

/**
 * A slot key turned into its signer's name — the first declaration of the key,
 * which is the one the Signers panel is already showing. A signer with no name
 * is named by its row there instead, so two unnamed signers still read as two.
 */
function nameSignatureSlot(
  slotKey: string,
  blocks: DocumentTemplateBlock[],
  t: TFunction
): string {
  const slots = declaredSlots(blocks)
  const position = slots.findIndex((slot) => slot.key === slotKey) + 1
  const name = slots[position - 1]?.name
  return (
    name?.trim() ||
    t("documentTemplates.signers.errors.slotPosition", {
      position: position || 1
    })
  )
}

// Every parameter is offered to every message and each message picks the ones
// it names, so a translation can put its subject where its grammar wants it.
function describeSignatureErrors(
  errors: SignatureBlockError[],
  blocks: DocumentTemplateBlock[],
  t: TFunction
): string[] {
  return errors.map(
    ({ blockId, slotKey, reason, fieldType, fieldKey, partyType }) =>
      t(SIGNATURE_RULE_KEYS[reason], {
        block: blockId ? nameSignatureBlock(blockId, blocks, t) : undefined,
        name: slotKey ? nameSignatureSlot(slotKey, blocks, t) : undefined,
        fieldType,
        fieldKey,
        partyType
      })
  )
}

export {
  SIGNATURE_BLOCK_TYPES,
  SIGNATURE_FIELD_TYPES,
  SIGNATURE_BLOCK_RULES,
  PARTY_TYPES,
  type SignerSlot,
  type SignatureBlockError,
  type SignatureBlockRule,
  type SignatureFieldDefinition,
  type SignatureFieldType,
  type PartyType,
  isSignatureBlock,
  slotOf,
  fieldsOf,
  declaredSlots,
  validateSignatureBlocks,
  blocksWithSlotRenamed,
  blocksWithSlotRemoved,
  mintSlotKey,
  describeSignatureErrors
}
